import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageVisit, updateVisitDuration } from '../utils/visitTracking';

/**
 * PageTracker component that automatically tracks page visits on route changes
 * Should be placed inside Router but outside individual routes
 */
const PageTracker = () => {
  const location = useLocation();
  const previousLocationRef = useRef();
  const cleanupRef = useRef();

  useEffect(() => {
    // Update duration for previous page if we have a visit ID
    if (previousLocationRef.current && previousLocationRef.current !== location.pathname) {
      updateVisitDuration();
    }

    // Track the new page visit
    const trackVisit = async () => {
      try {
        const additionalData = {
          route_change: true,
          previous_path: previousLocationRef.current || null,
          search_params: location.search || null,
          hash: location.hash || null
        };

        await trackPageVisit(additionalData);
        
        // Set up visit duration tracking for this page
        const setupDurationTracking = () => {
          // Store visit start time
          sessionStorage.setItem('visitStartTime', Date.now().toString());
          
          // Track when user leaves the page
          const handleBeforeUnload = () => {
            const visitId = sessionStorage.getItem('currentVisitId');
            const startTime = sessionStorage.getItem('visitStartTime');
            
            if (visitId && startTime) {
              const duration = Math.floor((Date.now() - parseInt(startTime)) / 1000);
              
              if (duration > 0 && navigator.sendBeacon) {
                const data = new FormData();
                data.append('visit_id', visitId);
                data.append('duration', duration);
                navigator.sendBeacon('/api/track/visit/duration', data);
              }
            }
          };
          
          // Track when page becomes hidden/visible
          const handleVisibilityChange = () => {
            if (document.hidden) {
              updateVisitDuration();
            } else {
              const visitId = sessionStorage.getItem('currentVisitId');
              if (visitId) {
                sessionStorage.setItem('visitStartTime', Date.now().toString());
              }
            }
          };
          
          window.addEventListener('beforeunload', handleBeforeUnload);
          document.addEventListener('visibilitychange', handleVisibilityChange);
          
          return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
        };
        
        // Clean up previous listeners
        if (cleanupRef.current) {
          cleanupRef.current();
        }
        
        // Set up new listeners
        cleanupRef.current = setupDurationTracking();
        
      } catch (error) {
        console.error('Error tracking page visit:', error);
      }
    };

    trackVisit();
    
    // Update previous location reference
    previousLocationRef.current = location.pathname;
    
    // Cleanup on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [location]);

  // This component doesn't render anything
  return null;
};

/**
 * Higher-order component that adds automatic page tracking to any component
 */
export const withAutoTracking = (WrappedComponent, pageData = {}) => {
  return function AutoTrackingWrapper(props) {
    const location = useLocation();
    
    useEffect(() => {
      const additionalData = {
        component: WrappedComponent.displayName || WrappedComponent.name || 'UnknownComponent',
        ...pageData
      };
      
      trackPageVisit(additionalData);
    }, [location.pathname]);
    
    return <WrappedComponent {...props} />;
  };
};

/**
 * Custom hook for manual page tracking with additional data
 */
export const usePageVisit = (additionalData = {}, dependencies = []) => {
  const location = useLocation();
  
  useEffect(() => {
    trackPageVisit(additionalData);
  }, [location.pathname, ...dependencies]);
};

export default PageTracker;